type ShowcaseConfig = {
  numberOfProjectsPerPage: number;
};

export const showcaseConfig: ShowcaseConfig = {
  numberOfProjectsPerPage: 12,
};

type ShowCaseSeoMetaData = {
  pageTitle: string;
  pageDescription: string;
  mainHeadingText: string;
  introParagraphText: string;
};

type ShowcaseCollectionsConfig = {
  fallbackSeoMetadata: ShowCaseSeoMetaData;
};

export const showcaseCollectionsConfig: ShowcaseCollectionsConfig = {
  fallbackSeoMetadata: {
    pageTitle: 'Best Webflow websites for web design inspiration',
    pageDescription:
      "Discover beautiful examples of Webflow websites and templates that are sure to give you the unique web design inspiration you're looking for.",
    mainHeadingText: 'Discover the best Webflow websites',
    introParagraphText:
      'Browse through unique examples of Webflow websites, templates, and cloneables — designed by the Webflow Community.',
  },
};

type ShowcaseSitesConfig = {
  noindexSiteProfileSlugs: {
    [key: string]: boolean;
  };
};

export const showcaseSitesConfig: ShowcaseSitesConfig = {
  // A list of Site Profiles for which we don't want their Showcase pages to be indexed by search engine crawlers.
  // Introduced for https://webflow.atlassian.net/browse/ACQ-181
  //
  // We are aware this list of slugs is environment specific and should rather be defined in "config/<env>.js" files,
  // but we cannot access those files on Marketplace app yet. Fortunately, we can access SharedConfig there,
  // therefore the list is defined here and contains production slugs only.
  //
  // Please keep this list in form of a dictionary, where **keys are lower-case** and value is always true.
  // We decided for a dictionary instead of a list for a simplified access in the usage places.
  //
  noindexSiteProfileSlugs: {
    'webinar-landing-page': true,
  },
};

type ShowcaseTagConfig = {
  seoTemplates: ShowCaseSeoMetaData;
  noindexTags: string[];
  tagLabels: {
    [key: string]: string;
  };
  preferredSynonyms: {
    [key: string]: string;
  };
};

export const showcaseTagConfig: ShowcaseTagConfig = {
  seoTemplates: {
    pageTitle: 'The best %tag_label% websites',
    pageDescription:
      'Discover the best %tag_label% websites created by professional designers. Get inspired and start planning your perfect %tag_label% web design today!',
    mainHeadingText: 'Discover %tag_label% websites from the Webflow community',
    introParagraphText:
      'The Webflow Showcase is the place to find, clone, follow, like, and comment on the latest community sites.',
  },

  // List of tags for which we don't want their pages to be indexed by search engines.
  // Some of them come from  https://docs.google.com/spreadsheets/d/1tqIGPtL7okfS_2ahUISmYLe177mDrpMpqnynrWDxaY4/edit#gid=1050757081 .
  //
  // Please, keep this list ordered alphabetically and make all entries lowercase.
  //
  noindexTags: [
    'adobe muse',
    'anuss',
    'arse',
    'arsehole',
    'ass',
    'assbag',
    'assbandit',
    'assbanger',
    'assbite',
    'assclown',
    'asscock',
    'asscracker',
    'asses',
    'assface',
    'assfuck',
    'assfucker',
    'assgoblin',
    'asshat',
    'asshead',
    'asshole',
    'asshopper',
    'assjacker',
    'asslick',
    'asslicker',
    'assmonkey',
    'assmunch',
    'assmuncher',
    'assnigger',
    'asspirate',
    'assshit',
    'assshole',
    'asssucker',
    'asswad',
    'asswipe',
    'axwound',
    'bampot',
    'bangladesh-escort',
    'bastard',
    'bd-escort',
    'beaner',
    'binance',
    'binancecustomercarenumber',
    'binancenumber',
    'binancephonenumber',
    'binancesupport',
    'binancesupportnumber',
    'binancesupportphonenumber',
    'bitch',
    'bitchass',
    'bitches',
    'bitchtits',
    'bitchy',
    'blow job',
    'blowjob',
    'bollocks',
    'bollox',
    'boner',
    'brix',
    'brotherfucker',
    'bullshit',
    'bumblefuck',
    'butt plug',
    'butt',
    'buttfucka',
    'buttfucker',
    'call girl',
    'call-girl',
    'callgirl',
    'camel toe',
    'cannabis',
    'cannabis industry',
    'cannabis-industry',
    'cannabisindustry',
    'cashappcancel',
    'cashappcontactinfonumber',
    'cashappcontactnumber',
    'cashappcustomercare',
    'cashappcustomercarenumber',
    'cashappcustomerservfice',
    'cashappcustomerservice',
    'cashappcustomersupport',
    'cashapphelplinenumber',
    'cashapphelpnumber',
    'cashapplogin',
    'cashappnuber',
    'cashappnumber',
    'cashappnumbr',
    'cashappphone',
    'cashappphonenumber',
    'cashapprefund',
    'cashapprefundnumber',
    'cashappsuportnumber',
    'cashappsupport',
    'cashappsupportnumber',
    'cashappsupportnumer',
    'cashappsupportphone',
    'cashappsupportphonenumber',
    'cashapptech',
    'cbd',
    'dhaka',
    'dhaka-escort',
    'dreamweaver',
    'envato',
    'escort',
    'fascism',
    'fascist',
    'finsweet',
    'flowbase',
    'fuck',
    'fucking',
    'hot girl',
    'hot girls',
    'hot-girl',
    'hot-girls',
    'instagramphonenumber',
    'instagramsupportnumber',
    'jetboost',
    'jomor',
    'joseph berry',
    'kike',
    'kooch',
    'kootch',
    'krakensupportnumber',
    'kraut',
    'kunt',
    'kyke',
    'lameass',
    'lardass',
    'lesbian',
    'lesbo',
    'lezzie',
    'marijuana',
    'masturbate',
    'mcfagget',
    'mick',
    'minge',
    'mothafucka',
    "mothafuckin'",
    'motherfucker',
    'motherfucking',
    'muff',
    'muffdiver',
    'munging',
    'murder',
    'nazi',
    'negro',
    'nigaboo',
    'nigga',
    'nigger',
    'niggerish',
    'niggers',
    'niglet',
    'nignog',
    'nsfw',
    'nut sack',
    'nutsack',
    'paki',
    'paypalcreditphonenumber',
    'paypalsupportnumber',
    'phish',
    'phishing',
    'porn',
    'qualitybacklinks',
    'racism',
    'racist',
    'rape',
    'relume',
    'sex',
    'sexy',
    'sexy-forms',
    'shit',
    'sucks',
    't rick',
    't ricks',
    't ricks',
    't rickz',
    't tricks',
    't-ricks',
    't. ricks',
    't.ricks',
    't.ricks',
    'thimothy ricks',
    'timmothy ricks',
    'timmothy ricksd',
    'timophy ricks',
    'timothy ric',
    'timothy rick',
    'timothy ricks',
    'timothy ricksd',
    'timothy rickw',
    'timothy ricky',
    'timothy ricls',
    'timothy rics',
    'timothy ricsk',
    'timothy riks',
    'timothy',
    'timothyricks',
    'timothyricks',
    'tinderhelp',
    'tomothy ricks',
    'tricks',
    'ttricks',
    'tycreated',
    'tymothy ricks',
    'webflow',
    'wizardry',
  ],

  // Map in form of:
  //   tag -> label
  // - Labels are meant to be used in H1s, page titles, etc.
  // - Please be not surprised by some labels defined same as their tags – it might be to prevent hyphens being
  //   replaced by spaces which happens by default when generating a label out of a tag.
  tagLabels: {
    austin: 'Austin',
    australia: 'Australia',
    'covid-19': 'covid-19',
    dallas: 'Dallas',
    'front-end': 'front-end',
    houston: 'Houston',
    japanese: 'Japanese',
    'los-angeles': 'Los Angeles',
    'multi-language': 'multi-language',
    'multi-step-form': 'multi-step form',
    'new-york': 'New York',
    'new-york-city': 'New York City',
    'new-zealand': 'New Zealand',
    'no-code': 'no-code',
    'pop-ups': 'pop-ups',
    russian: 'Russian',
    'san-francisco': 'San Francisco',
    'ux-ui': 'UX/UI',
  },

  // Map in form of:
  //   tag -> tag to use instead
  preferredSynonyms: {
    // Groups added to prevent URL clash of Tag Page URL "/websites/:tag" with other pages like "/websites/popular"
    popular: 'general',
    following: 'follow',
    recent: 'latest',
    likes: 'liked',
    cloneable: 'clone',
    collections: 'collection',
    // Other synonyms.
    // Some of them are here only to prevent adding tags with space encoded as "%20" to a sitemap.
    // Please, keep it ordered alphabetically by *values*, not by keys.
    '3d animation': '3d',
    '3d effect': '3d',
    '3d modelling': '3d',
    '3dart': '3d',
    '3dcards': '3d',
    '3deffect': '3d',
    '3dmodel': '3d',
    '3drendering': '3d',
    '3dscroll': '3d',
    '3dtransforms': '3d',
    '3dweb': '3d',
    'accordion tab': 'accordion',
    'accounting services': 'accounting',
    ads: 'advertising',
    'affordable website design': 'affordable-website-design',
    'after effects': 'after-effects',
    agencies: 'agency',
    agencywebsite: 'agency',
    animate: 'animation',
    animated: 'animation',
    animations: 'animation',
    animaton: 'animation',
    webflowanimation: 'animation',
    'app design': 'app',
    appdesign: 'app',
    application: 'app',
    apps: 'app',
    'mobile apps': 'app',
    'web apps': 'app',
    webapp: 'app',
    architect: 'architecture',
    architects: 'architecture',
    arts: 'art',
    'art & illustration': 'art-and-illustration',
    'art direction': 'art-and-illustration',
    'art gallery': 'art-and-illustration',
    'art director': 'art-director',
    articles: 'article',
    artificialintelligence: 'artificial-intelligence',
    artist: 'artistic',
    artists: 'artistic',
    artistwebsite: 'artistic',
    artwork: 'artistic',
    'audio visual': 'audio-visual',
    augmentedreaity: 'augmented-reality',
    augmentedreality: 'augmented-reality',
    austinwebflow: 'austin',
    webflowaustin: 'austin',
    webflowaustralia: 'australia',
    'video background': 'backgorund-video',
    'background image': 'background-image',
    backgroundvideo: 'background-video',
    videobackground: 'background-video',
    barber: 'barber-shop',
    barbershop: 'barber-shop',
    'beauty studio': 'beauty',
    beautysalon: 'beauty',
    bigdata: 'big-data',
    'black & white': 'black-and-white',
    'black and white': 'black-and-white',
    'blog template': 'blog',
    blogger: 'blog',
    blogging: 'blog',
    blogsite: 'blog',
    bookings: 'booking',
    books: 'booking',
    'brand strategy': 'brand-strategy',
    'branding expert': 'branding',
    brandingstudio: 'branding',
    rebrand: 'branding',
    rebranding: 'branding',
    brandingagency: 'branding-agency',
    'branding consultant': 'branding-consultant',
    'branding identity design': 'branding-identity-design',
    webbuild: 'build',
    burgers: 'burger',
    busines: 'business',
    businesssite: 'business',
    businesswebsite: 'business',
    'business card': 'business-card',
    'business landing page': 'business-landing-page',
    buttons: 'button',
    buttonanimation: 'button-animation',
    buttonhover: 'button-animation',
    'cannabis industry': 'cannabis',
    cars: 'car',
    card: 'cards',
    'cars dealer': 'cars-dealer',
    'case study': 'case-study',
    casestudies: 'case-study',
    casestudy: 'case-study',
    'church online': 'church',
    churchtemplate: 'church',
    churchwebsite: 'church',
    'clean design': 'clean',
    cleandesign: 'clean',
    'house cleaning': 'cleaning',
    onclick: 'click-interactions',
    clientwork: 'client-work',
    climatechange: 'climate-change-related',
    "men's clothing": 'clothing',
    "women's clothing": 'clothing',
    wbflowcms: 'cms',
    cmsgallery: 'cms-gallery',
    'business coaching': 'coaching',
    'life coach': 'coaching',
    codepen: 'code-pen',
    'coffee shop': 'coffee-shop',
    coffeeshop: 'coffee-shop',
    colors: 'colorful',
    colour: 'colorful',
    colourful: 'colorful',
    'coming soon': 'coming-soon',
    'coming soon page': 'coming-soon',
    comingsoon: 'coming-soon',
    'company site': 'company',
    companypage: 'company',
    'complex navigation': 'complex-navigation',
    components: 'component',
    'concept design': 'concept-design',
    'construction website': 'construction',
    'business consulting': 'consulting',
    consultancy: 'consulting',
    consultant: 'consulting',
    'management consulting': 'consulting',
    'technology consulting': 'consulting',
    webconsulting: 'consulting',
    'contact form': 'contact-form',
    'content marketing': 'content',
    contentcreator: 'content',
    'cool effects': 'cool-effects',
    copywriter: 'copywriting',
    copywriting: 'copywriting',
    'corporate events': 'corporate',
    corporatewebsite: 'corporate',
    'corporate identity': 'corporate-identity',
    course: 'courses',
    covid: 'covid-19',
    covid19: 'covid-19',
    covid19helper: 'covid-19',
    'craft beer': 'craft-beer',
    creatives: 'creative',
    creativeagency: 'creative-agency',
    'creative designs': 'creative-designs',
    creativedirector: 'creative-director',
    'css animation': 'css-animations',
    cssanimation: 'css-animations',
    cssgrid: 'css-grid',
    grid: 'css-grid',
    'grid design': 'css-grid',
    'grid layout': 'css-grid',
    grids: 'css-grid',
    'custom buttons': 'custom-buttons',
    'custom code': 'custom-code',
    customcode: 'custom-code',
    customcursor: 'custom-cursor',
    'custom modal': 'custom-modal',
    dailyinteraction: 'daily-interaction',
    dailyinteractions: 'daily-interaction',
    dallaswebflow: 'dallas',
    webflowdallas: 'dallas',
    darkmode: 'dark-mode',
    darktheme: 'dark-mode',
    dashboards: 'dashboard',
    dentalclinic: 'dental',
    dentistry: 'dentist',
    desgin: 'design',
    designer: 'design',
    designers: 'design',
    designs: 'design',
    designstudio: 'design',
    'design agency': 'design-agency',
    'design studio': 'design-agency',
    designagency: 'design-agency',
    'design blog': 'design-blog',
    'design portfolio': 'design-portfolio',
    designerportfolio: 'design-portfolio',
    designportfolio: 'design-portfolio',
    designsystem: 'design-system',
    'design thinking': 'design-thinking',
    designthinking: 'design-thinking',
    webflowdesigner: 'designer',
    webflowdeveloper: 'developer',
    develoment: 'development',
    digitalagency: 'digital-agency',
    digitalbutlers: 'digital-agency',
    'digital creative': 'digital-creative',
    'digital design': 'digital-design',
    digitaldesign: 'digital-design',
    digitaldesigner: 'digital-design',
    digital: 'digital-marketing',
    'digital marketing': 'digital-marketing',
    'marketing digital': 'digital-marketing',
    digitalproducts: 'digital-products',
    'digital strategy': 'digital-strategy',
    'discover startups': 'discover-startups',
    doctors: 'doctor',
    donate: 'donation',
    'e-commerce': 'ecommerce',
    eccomerce: 'ecommerce',
    webflowecommerce: 'ecommerce',
    educational: 'education',
    effect: 'effects',
    'email capture': 'email-capture',
    'email marketing': 'email-marketing',
    'energy industry': 'energy-industry',
    events: 'event',
    'local events': 'event',
    'event planning': 'event-planning',
    'experience design': 'experience-design',
    faqs: 'faq',
    'fashion website': 'fashion',
    figma2webflow: 'figma',
    figmatowebflow: 'figma',
    filmmaker: 'filmmaking',
    filtering: 'filter',
    filters: 'filter',
    financial: 'finance',
    invest: 'financial',
    investing: 'financial',
    investment: 'financial',
    investor: 'financial',
    'financial services': 'financial-services',
    'fixed menu': 'fixed-menu',
    'flat design': 'flat',
    fonts: 'font',
    'food & drink': 'food',
    foodblog: 'food',
    foodie: 'food',
    'healthy food': 'food',
    'for hire': 'for-hire',
    form: 'forms',
    foxycart: 'foxy-cart',
    'free template': 'free-template',
    freebies: 'freebie',
    freelace: 'freelance',
    freelancer: 'freelance',
    freelancers: 'freelance',
    freelancing: 'freelance',
    freelancedesigner: 'freelance-designer',
    'freelance web designer': 'freelance-web-designer',
    frontenddevelopment: 'front-end',
    fullpage: 'full-page',
    fullpagejs: 'full-page-js',
    'full screen': 'full-screen',
    fullscreen: 'full-screen',
    'full site': 'full-site',
    'full width': 'full-width',
    'fullscreen menu': 'fulll-screen-menu',
    future: 'futuristic',
    game: 'gaming',
    games: 'gaming',
    videogame: 'gaming',
    googleads: 'google-ads',
    googlemaps: 'google-maps',
    gradients: 'gradient',
    graphics: 'graphic',
    'graphic design': 'graphic-design',
    graphicdesign: 'graphic-design',
    'graphic designer': 'graphic-designer',
    graphicdesigner: 'graphic-designer',
    'graphic studio': 'graphic-studio',
    'growth hacking': 'growth-hacking',
    hairdresser: 'hair-dresser',
    'hair salon': 'hair-salon',
    'hair stylist': 'hair-stylist',
    hamburgermenu: 'hamburger-menu',
    hiphop: 'hip-hop',
    'hire me': 'hire-me',
    horizontal: 'horizontal-scroll',
    horizontalscroll: 'horizontal-scroll',
    hotels: 'hotel',
    home: 'housing',
    homes: 'housing',
    house: 'housing',
    houstonwebflow: 'houston',
    hover: 'hover-animations',
    'hover effects': 'hover-animations',
    hoveranimation: 'hover-animations',
    hovereffect: 'hover-animations',
    'human resources': 'human-resources',
    icons: 'icon',
    illustrations: 'illustration',
    imagehover: 'imagine-hover',
    madeinindia: 'Indian',
    industry: 'industrial ',
    'industrial design': 'industrial-design',
    information: 'information-technology',
    'information technology': 'information-technology',
    innovation: 'innovative',
    instagam: 'instagram',
    'instagram feed': 'instagram-feed',
    'interaction design': 'interaction-design',
    interactiondesign: 'interaction-design',
    interaction: 'interactions',
    webflowinteractions: 'interactions',
    webflowix2: 'interactions',
    interative: 'interactive',
    interfacedesign: 'interface-design',
    interior: 'interior-design',
    'interior design': 'interior-design',
    interiordesign: 'interior-design',
    interiors: 'interior-design',
    iosapp: 'ios',
    japan: 'japanese',
    jewellery: 'jewelry',
    job: 'job-seeking',
    'job seeker': 'job-seeking',
    jobboard: 'job-seeking',
    jobs: 'job-seeking',
    landing: 'landing-page',
    'landing page': 'landing-page',
    landingpage: 'landing-page',
    webflowlandingpage: 'landing-page',
    'landscape design': 'landscape-design',
    'large images': 'large-images',
    law: 'law-firm',
    'law firm': 'law-firm',
    lawyer: 'law-firm',
    lawyers: 'law-firm',
    'lead generation': 'lead-generation',
    leads: 'lead-generation',
    'leadership development': 'leadership-development',
    learn: 'learning-and-development',
    learning: 'learning-and-development',
    live: 'livestream',
    livestream: 'livestream',
    logo: 'logo-design',
    'logo design': 'logo-design',
    logos: 'logo-design',
    loop: 'loop-animation',
    'los angeles': 'los-angeles',
    lottie: 'lottie-file',
    lottiefiles: 'lottie-file',
    lottiejs: 'lottie-file',
    lotties: 'lottie-file',
    madeinwebflow: 'made-in-webflow',
    madewithwebflow: 'made-in-webflow',
    nativewebflow: 'made-in-webflow',
    webflowsites: 'made-in-webflow',
    webflowwebsite: 'made-in-webflow',
    maps: 'map',
    'best marketing agency': 'marketing-agency',
    'marketing agency': 'marketing-agency',
    marketingdesign: 'marketing-design',
    'material design': 'material-design',
    'media agency': 'media-agency',
    'medical professional': 'medical-professional',
    meetup: 'meet-up',
    megamenu: 'mega-menu',
    member: 'membership',
    memberships: 'membership',
    mentalhealth: 'mental-health',
    'mentes millonarias': 'mentes-millonarias',
    menuconcept: 'menu',
    menus: 'menu',
    microinteraction: 'microinteractions',
    microinteractions: 'microinteractions',
    minimal: 'minimalist',
    minimalism: 'minimalist',
    minimalistwebsite: 'minimalist',
    mobile: 'mobile-design',
    'mobile design': 'mobile-design',
    mobilefirst: 'mobile-design',
    'mobile friendly': 'mobile-friendly',
    'mobile landing': 'mobile-landing',
    'mobile menu': 'mobile-menu',
    mockups: 'mockup',
    modernecommerce: 'modern',
    'motion design': 'motion-design',
    motiondesign: 'motion-design',
    'motion graphics': 'motion-graphics',
    mousehover: 'mouse-hover-interaction',
    mousemovement: 'mouse-movement',
    mouseover: 'mouseover-interaction',
    movies: 'movie',
    multilanguage: 'multi-language',
    multilingual: 'multi-language',
    'multilingual website': 'multi-language',
    'multi-step form': 'multi-step-form',
    'website for music': 'music',
    'music & sound': 'music-and-sound',
    'music producer': 'music-producer',
    musicians: 'musician',
    nav: 'navbar',
    navigation: 'navbar',
    navbarinteractions: 'navbar-interactions',
    network: 'networking',
    newyork: 'new-york',
    'new york city': 'new-york-city',
    'new zealand': 'new-zealand',
    nocode: 'no-code',
    nocodetribe: 'no-code-tribe',
    nonprofits: 'nonprofit',
    'nuvi global': 'nuvi-global',
    'one page': 'one-page',
    'one page landing site': 'one-page',
    'one page website': 'one-page',
    onepage: 'one-page',
    onepagelove: 'one-page',
    onepager: 'one-page',
    Onepager: 'one-page',
    onepagesite: 'one-page',
    onepagewebsite: 'one-page',
    'single page': 'one-page',
    singlepage: 'one-page',
    'online courses': 'online-course',
    'online education': 'online-education',
    'online marketing': 'online-marketing',
    'online shopping': 'online-shopping',
    onlinestore: 'online-store',
    orangecattle: 'orange-cattle',
    'packaging design': 'packaging-design',
    pageload: 'page-load',
    'page transition': 'page-transitions',
    pagetransitions: 'page-transitions',
    paintings: 'painting',
    parallax: 'parallax-scroll-animations',
    parallaxanimation: 'parallax-scroll-animations',
    parallaxeffect: 'parallax-scroll-animations',
    parallaxscroll: 'parallax-scroll-animations',
    payments: 'payment',
    'personal branding': 'personal-branding',
    'personal trainer': 'personal-trainer',
    'personal training': 'personal-trainer',
    personal: 'personal-website',
    'personal site': 'personal-website',
    'personal website': 'personal-website',
    photos: 'photo-gallery',
    photographer: 'photography',
    photographers: 'photography',
    'photography website': 'photography',
    'website for photography': 'photography',
    photographyportfolio: 'photography-portfolio',
    piterdimitrov: 'Piter-dimitrov',
    play: 'playground',
    player: 'playground',
    playful: 'playground',
    podcasts: 'podcast',
    popup: 'pop-ups',
    porfolio: 'portfolio',
    portfoilio: 'portfolio',
    portfoliowebsite: 'portfolio',
    portoflio: 'portfolio',
    portfoliocourse: 'portfolio-course',
    'portrait photographer': 'portrait-photography',
    preloader: 'pre-load-animation',
    'pricing table': 'pricing-table',
    'print design': 'print-design',
    'product design': 'product-design',
    productdesign: 'product-design',
    'product designer': 'product-designer',
    productdesigner: 'product-designer',
    productmanagement: 'product-management',
    productpage: 'product-page',
    pro: 'professional',
    progressbar: 'progress-bar',
    projects: 'project',
    'project management': 'project-management',
    promotion: 'promotional',
    'property management': 'property-management',
    prototype: 'prototyping',
    'public relations': 'public-relations',
    'pure webflow': 'pure-webflow',
    'real estate': 'real-estate',
    'real estate agent': 'real-estate',
    realestate: 'real-estate',
    recipes: 'recipe',
    'record label': 'record-label',
    recruitment: 'recruiting',
    'recruitment agency': 'recruitment',
    rentals: 'rental',
    mobileresponsiveness: 'responsive-design',
    'responsive design': 'responsive-design',
    'responsive web': 'responsive-design',
    'responsive web design': 'responsive-design',
    'responsive websites': 'responsive-design',
    responsivedesign: 'responsive-design',
    responsivewebdesign: 'responsive-design',
    'restaurant website': 'restaurant',
    reviews: 'review',
    russia: 'russian',
    saasdesigner: 'saas',
    salespage: 'sales',
    'san francisco': 'san-francisco',
    sanfrancisco: 'san-francisco',
    whilescrolling: 'scroll',
    scroll: 'scroll-animation',
    scrollanimation: 'scroll-animation',
    scrolleffect: 'scroll-animation',
    scrolleffects: 'scroll-animation',
    scrollinteraction: 'scroll-animation',
    scrollbar: 'scroll-bar',
    scroller: 'scrolling',
    scrollintoview: 'scrolling',
    scrollmagic: 'scrolling',
    'scrolling website': 'scrolling-website',
    'search engine': 'search',
    sections: 'section',
    services: 'service',
    'shopping cart': 'shopping-cart',
    'sign up': 'sign-up',
    cmsslider: 'slider',
    customslider: 'slider',
    'fullpage slider': 'slider',
    'small business': 'small-business',
    'small business marketing': 'small-business',
    smallbusiness: 'small-business',
    social: 'social-media',
    'social media': 'social-media',
    'social media management': 'social-media',
    'social media marketing': 'social-media',
    socialmedia: 'social-media',
    'splash page': 'splash-page',
    'start-up': 'startup',
    'startup design': 'startup',
    startups: 'startup',
    storefront: 'store',
    story: 'storytelling',
    storybrand: 'storytelling',
    'strategic design': 'strategic-design',
    styleguide: 'style-guide',
    svganimation: 'svg-animation',
    templates: 'template',
    webtemplates: 'template',
    testimonials: 'testimonial',
    textanimation: 'text-animation',
    texteffects: 'text-animation',
    themes: 'theme',
    'throughflow labs': 'throughflow-labs',
    tools: 'tool',
    tours: 'tour',
    transition: 'transitions',
    transitionsanimations: 'transitions',
    'travel website': 'travel',
    travelblog: 'travel',
    traveling: 'travel',
    trucking: 'truck',
    'ui design': 'ui-design',
    uidesign: 'ui-design',
    uidesigner: 'ui-design',
    'ui kit': 'ui-kit',
    uikit: 'ui-kit',
    uiportfolio: 'ui-portfolio',
    'united kingdom': 'united-kingdom',
    'user centered design': 'user-centered-design',
    'user experience': 'user-experience',
    userexperience: 'user-experience',
    userfriendly: 'user-friendly',
    'user interaction': 'user-interaction',
    'user interface': 'user-interface',
    userinterface: 'user-interface',
    'user testing': 'user-testing',
    uxagency: 'ux-agency',
    'user experience design': 'ux-design',
    'ux design': 'ux-design',
    uxdesign: 'ux-design',
    uxdesigner: 'ux-designer',
    uxportfolio: 'ux-portfolio',
    uxresearch: 'ux-research',
    'ui/ux': 'ux-ui',
    uiux: 'ux-ui',
    uix: 'ux-ui',
    'ux/ui': 'ux-ui',
    uxui: 'ux-ui',
    'venture capital': 'venture-capital',
    'vertical nav': 'vertical-nav',
    videos: 'video',
    'video editing': 'video-editing',
    'video games': 'video-games',
    'video production': 'video-production',
    videoproduction: 'video-production',
    'virtual reality': 'virtual-reality',
    virtualreality: 'virtual-reality',
    'visual design': 'visual-design',
    'visual designer': 'visual-designer',
    'visual identity': 'visual-idenity',
    'web deisgn': 'web-deisgn',
    'diseño web': 'web-design',
    'web design': 'web-design',
    'web-design': 'web-design',
    webdesig: 'web-design',
    webdesign: 'web-design',
    webagency: 'web-design-agency',
    webdesignagency: 'web-design-agency',
    webflowagency: 'web-design-agency',
    'web designer': 'web-designer',
    webdesigner: 'web-designer',
    'web desing': 'web-design',
    'web developer': 'web-developer',
    'web development': 'web-development',
    webdevellopment: 'web-development',
    webdeveloper: 'web-development',
    webdevelopment: 'web-development',
    webdevelpment: 'web-development',
    webpage: 'web-page',
    webshop: 'web-shop',
    webstore: 'web-store',
    webdevforyou: 'webdev-for-you',
    webfow: 'webflow',
    webflowcms: 'webflow-cms',
    'webflow experiment': 'webflow-experiment',
    'webflow template': 'webflow-template',
    'webflow tutorial': 'webflow-tutorial',
    webflowuniversity: 'webflow-university',
    'site design': 'website-design',
    'website design': 'website-design',
    'website designer': 'website-designer',
    websitedesigner: 'website-designer',
    'website maker': 'website-maker',
    'website template': 'website-template',
    weddings: 'wedding',
    wellbeing: 'well-being',
    wellness: 'wellness',
    wireframe: 'wireframing',
    wireframes: 'wireframing',
    'wordpress themes': 'wordpress-themes',
    writer: 'writing',
    'yoga studio': 'yoga-studio',
  },
};

type University = {
  maxCoursesPerUser: number;
};

export const university: University = {maxCoursesPerUser: 50};
